/**
 * Agreement Service
 *
 * The platform commitments a tenant is asked to accept — after sign-up, and before its first
 * machine. The backend enforces these itself (machine creation is refused while one is
 * outstanding), so this is the console's only way to let the customer clear the block.
 */

import { api } from "@/lib/api";

/** Slugs the backend uses; mirrors AgreementRegistry.Slugs. */
export const AGREEMENT_TYPES = [
  "signup_commitment",
  "vm_creation_commitment",
] as const;
export type AgreementType = (typeof AGREEMENT_TYPES)[number];

export interface AgreementTranslationDto {
  /** Language code, "vi" or "en". */
  culture: string;
  title: string;
  /** Operator-authored HTML. Always sanitise before rendering. */
  bodyHtml: string;
}

export interface AgreementDto {
  type: AgreementType;
  version: number;
  /**
   * Every language this revision was published in, always at least one.
   *
   * All of them arrive together so the dialog's language switch is instant and both languages are
   * guaranteed to be the same revision — they are one document, fetched once.
   */
  translations: AgreementTranslationDto[];
  /** Covers every language, so it identifies the revision rather than one rendering of it. */
  contentHash: string;
}

/**
 * Drops anything the dialog could not actually render.
 *
 * The gate blocks the whole console, so a payload it cannot read has to degrade rather than
 * throw: a missing `translations` array used to reach `pickInitialCulture` and crash the app into
 * an error page, locking the customer out of everything instead of only the commitment. The
 * likeliest cause is a server serving an older shape than the console expects, so it is logged
 * loudly — but the customer keeps a working console, and creation stays blocked server-side
 * regardless of what the UI manages to show.
 */
function normaliseAgreements(agreements: AgreementDto[] | null): AgreementDto[] {
  if (!Array.isArray(agreements)) {
    console.error("[agreements] Unexpected response shape; ignoring.", agreements);
    return [];
  }

  return agreements.filter((agreement) => {
    const usable =
      Array.isArray(agreement?.translations) &&
      agreement.translations.some((entry) => entry?.bodyHtml);

    if (!usable) {
      console.error(
        "[agreements] Commitment has no readable text; skipping it.",
        agreement,
      );
    }

    return usable;
  });
}

export const agreementService = {
  // The shared `api` helper already prefixes /api/v1 and unwraps the StandardResponse
  // envelope, so these return the payload directly.

  /** Commitments this tenant still has to accept. Empty when both flags are off. */
  getOutstanding: async (): Promise<AgreementDto[]> => {
    const agreements = await api.get<AgreementDto[]>("/agreements/outstanding");
    return normaliseAgreements(agreements);
  },

  /**
   * Records acceptance. The version and hash that were displayed are echoed back; the backend
   * refuses the write if they no longer match the current revision, so a console left open
   * across a change cannot record agreement to wording that was never on screen.
   */
  accept: (agreement: AgreementDto, culture: string): Promise<unknown> =>
    api.post("/agreements/accept", {
      type: agreement.type,
      version: agreement.version,
      contentHash: agreement.contentHash,
      // Which language was on screen. Evidence only — both are the same revision — but it is the
      // part a customer would later dispute.
      culture,
    }),
};
